import { useCallback, useReducer, useRef } from 'react'
import debounce from 'lodash/debounce'
import { getPlacesAutocomplete, getPlaceDetails } from 'APP/Lib/PlacesAutocompleteHelpers'
import Config from 'APP/Config'
import { isCanada } from 'APP/Helpers/RegionHelpers'
import { getPlacesSupportedType } from 'APP/Lib/PlacesAutocompleteHelpers'
import { PlaceAutocomplete } from '@dialogue/coredata'

export type SearchResult = PlaceAutocomplete & {
  placeID: string
  label: string
  caption: string
}

type SearchState = {
  error?: unknown
  running: boolean
  results: SearchResult[]
  query?: string
}

type SearchAction =
  | { type: 'fetchStart'; query: string }
  | { type: 'fetchSuccess'; query: string; results?: SearchResult[] }
  | { type: 'fetchFailure'; error: unknown }
  | { type: 'resetState' }

export const initialSearchState: SearchState = {
  error: undefined,
  running: false,
  results: [],
  query: undefined,
}
export function SearchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'fetchStart':
      return {
        ...state,
        error: undefined,
        running: true,
        query: action.query,
      }
    case 'fetchSuccess':
      if (state.query === action.query) {
        return {
          ...state,
          error: undefined,
          running: false,
          results: action.results || initialSearchState.results,
        }
      }
      return {
        ...state,
        running: false,
      }
    case 'fetchFailure':
      return {
        ...state,
        error: action.error,
        running: false,
      }
    case 'resetState':
      return initialSearchState
    default:
      throw new Error('Unknown action passed to SearchReducer')
  }
}
const defaultSearchOptions = {
  ...(isCanada() && { country: Config.REGION }),
  type: 'address',
  placeFields: [
    'name',
    'addressComponents',
    'address',
    'openingHours',
    'phoneNumber',
    'location',
    'types',
    'website',
  ],
}
export const usePlacesSearch = (searchOptionsRequested = {}, debounceMs = 1000) => {
  const searchOptions = { ...defaultSearchOptions, ...searchOptionsRequested }

  const [state, dispatch] = useReducer(SearchReducer, initialSearchState)
  const callId = useRef<number>(null)

  const getPlacePredictions = async (query: string, abortSignal: AbortSignal) => {
    const places = await getPlacesAutocomplete(
      {
        searchTerm: query,
        placeType: getPlacesSupportedType(searchOptions.type),
      },
      { signal: abortSignal }
    )

    if (places?.length) {
      return places.map((place: PlaceAutocomplete) => ({
        ...place,
        placeID: place?.place_id,
        label: place?.name,
        caption: place?.address,
      }))
    }
    return []
  }

  const abortControllerRef = useRef<AbortController>(null)

  const debouncedFetch = useRef(
    debounce(async (query: string) => {
      const currentId = Date.now()
      callId.current = currentId

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      let results: SearchResult[] | undefined
      let error: unknown

      try {
        if (query) {
          results = await getPlacePredictions(query, abortController.signal)
        }
      } catch (e) {
        if (e instanceof Error && e.name !== 'AbortError') {
          error = e
        }
      }

      if (currentId === callId.current) {
        if (error) {
          dispatch({ type: 'fetchFailure', error })
        } else {
          dispatch({ type: 'fetchSuccess', query, results })
        }
      }
    }, debounceMs)
  ).current

  const resolvePlace = useCallback(async (placeID: string) => {
    const placeDetails = await getPlaceDetails(placeID)
    return placeDetails
  }, [])

  const submitQuery = useCallback((query: string) => {
    if (query) {
      dispatch({ type: 'fetchStart', query })
      debouncedFetch(query)
    } else {
      dispatch({ type: 'resetState' })
    }
  }, [])

  return [state, submitQuery, resolvePlace, getPlacePredictions] as const
}
